import type { ComponentProps, FunctionComponent } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  Cell,
  ColumnDef,
  ExpandedState,
  OnChangeFn,
  RowData,
  RowSelectionState,
  SortingState,
  TableOptions,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Collapsible,
  CollapsibleContent as ShadCNCollapsibleContent,
  ScrollArea,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components';
import { DefaultTableCell } from '@/components/atoms/DefaultTableCell';
import type { FunctionComponentWithChildren } from '@/common';
import { ctw } from '@/common';
import type { SortDirection } from '@ballerine/common';
import { isInstanceOfFunction } from '@ballerine/common';
import { checkIsBooleanishRecord } from '@/common/utils/check-is-booleanish-record/check-is-booleanish-record';
import { ChevronDown } from 'lucide-react';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Its external module that uses interface
  export interface ColumnMeta<TData extends RowData, TValue> {
    useWrapper?: boolean;
    headerClassName?: string;
  }
}

export type IDataTableProps<TData, TValue = any> = {
  data: TData[];
  columns: Array<ColumnDef<TData, TValue>>;
  caption?: ComponentProps<typeof TableCaption>['children'];

  CellContentWrapper?: FunctionComponentWithChildren<{ cell: Cell<TData, TValue> }>;
  CollapsibleContent?: FunctionComponent<{ row: TData }>;

  // Component props
  props?: {
    scroll?: Partial<ComponentProps<typeof ScrollArea>>;
    table?: ComponentProps<typeof Table>;
    header?: ComponentProps<typeof TableHeader>;
    head?: ComponentProps<typeof TableHead>;
    row?: ComponentProps<typeof TableRow>;
    body?: ComponentProps<typeof TableBody>;
    cell?: ComponentProps<typeof TableCell>;
    noDataCell?: ComponentProps<typeof TableCell>;
    caption?: ComponentProps<typeof TableCaption>;
    wrapper?: ComponentProps<'div'>;
  };

  // react-table options
  options?: Omit<TableOptions<TData>, 'getCoreRowModel' | 'data' | 'columns'>;

  sort?: {
    onSort: (params: { sortBy: string; sortDir: SortDirection }) => void;
    sortDir: SortDirection | undefined;
    sortBy: string | undefined;
  };

  select: {
    onSelect: (ids: Record<string, boolean>) => void;
    selected: Record<string, boolean>;
  };

  // External sorting state management (for backend sorting)
  sortingState?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;

  scrollRef?: React.RefObject<HTMLDivElement>;
  handleScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
};

const DataTableBase = <TData extends RowData, TValue = any>(
  {
    data,
    props,
    caption,
    columns,
    CellContentWrapper,
    options = {},
    CollapsibleContent,
    sort,
    select,
    sortingState,
    onSortingChange,
    handleScroll,
  }: IDataTableProps<TData, TValue>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const { enableSorting = false } = options;

  // Use external sorting state if provided, otherwise internal state
  const [internalSorting, setInternalSorting] = useState<SortingState>([
    {
      id: sort?.sortBy || options?.initialState?.sorting?.[0]?.id || 'id',
      desc: sort?.sortDir === 'desc' || options?.initialState?.sorting?.[0]?.desc || false,
    },
  ]);

  const sorting = sortingState || internalSorting;
  const setSorting = onSortingChange || setInternalSorting;

  useEffect(() => {
    if (sort?.sortBy && sort?.sortDir && !sortingState) {
      setInternalSorting([
        {
          id: sort.sortBy,
          desc: sort.sortDir === 'desc',
        },
      ]);
    }
  }, [sort?.sortBy, sort?.sortDir, sortingState]);

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    sortingUpdaterOrValue => {
      if (onSortingChange) {
        // Use external sorting state handler
        onSortingChange(sortingUpdaterOrValue);
      } else {
        // Legacy sorting with URL parameters
        setSorting(prevSortingState => {
          if (!isInstanceOfFunction(sortingUpdaterOrValue)) {
            sort?.onSort({
              sortBy: sortingUpdaterOrValue[0]?.id || sort?.sortBy || '',
              sortDir: sortingUpdaterOrValue[0]?.desc ? 'desc' : 'asc',
            });

            return sortingUpdaterOrValue;
          }

          const newSortingState = sortingUpdaterOrValue(prevSortingState);

          sort?.onSort({
            sortBy: newSortingState[0]?.id || sort?.sortBy || '',
            sortDir: newSortingState[0]?.desc ? 'desc' : 'asc',
          });

          return newSortingState;
        });
      }
    },
    [onSortingChange, setSorting, sort?.onSort, sort?.sortBy],
  );

  const { selected: ids, onSelect } = select;
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    checkIsBooleanishRecord(ids) ? ids : {},
  );

  const onRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
    selectionUpdaterOrValue => {
      setRowSelection(prevSelectionState => {
        if (!isInstanceOfFunction(selectionUpdaterOrValue)) {
          onSelect(selectionUpdaterOrValue);

          return selectionUpdaterOrValue;
        }

        const newSelectionState = selectionUpdaterOrValue(prevSelectionState);

        onSelect(newSelectionState);

        return newSelectionState;
      });
    },
    [onSelect],
  );

  useEffect(() => {
    if (Object.keys(ids ?? {}).length > 0) {
      return;
    }

    setRowSelection({});
  }, [ids]);

  const state = useMemo(
    () => ({
      rowSelection,
      ...(enableSorting && {
        sorting,
      }),
      ...(CollapsibleContent && {
        expanded,
      }),
    }),
    [CollapsibleContent, enableSorting, expanded, rowSelection, sorting],
  );

  const table = useReactTable<TData>({
    state,
    ...options,
    data: data ?? [],
    columns: columns ?? [],
    onRowSelectionChange,
    enableRowSelection: true,
    getRowId: row => (row as TData & { id: string }).id,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      cell: DefaultTableCell,
    },
    ...(enableSorting && {
      enableSorting,
      onSortingChange: handleSortingChange,
      manualSorting: true,
      sortDescFirst: true,
      enableSortingRemoval: false,
      getSortedRowModel: getSortedRowModel(),
    }),
    ...(CollapsibleContent
      ? {
          onExpandedChange: setExpanded,
          getExpandedRowModel: getExpandedRowModel(),
        }
      : {}),
  });

  return (
    <div
      className={ctw(
        'relative overflow-auto rounded-md border bg-white shadow',
        props?.wrapper?.className,
      )}
    >
      <ScrollArea orientation="both" {...props?.scroll} onScrollCapture={handleScroll} ref={ref}>
        <Table {...props?.table}>
          {caption && (
            <TableCaption
              {...props?.caption}
              className={ctw('text-foreground', props?.caption?.className)}
            >
              {caption}
            </TableCaption>
          )}
          <TableHeader className="border-0" {...props?.header}>
            {table.getHeaderGroups()?.map(({ id, headers }) => (
              <TableRow
                key={id}
                {...props?.row}
                className={ctw('border-b-none', props?.row?.className)}
              >
                {headers?.map((header, index) => {
                  return (
                    <TableHead
                      key={header.id}
                      {...props?.head}
                      className={ctw(
                        'sticky top-0 z-0 h-[34px] bg-white px-0 py-1 text-[14px] font-bold text-[#787981]',
                        {
                          '!pl-3.5': index === 0,
                        },
                        props?.head?.className,
                        header.column.columnDef.meta?.headerClassName,
                      )}
                    >
                      {header.column.id === 'select' && !header.isPlaceholder && (
                        <span className={'pe-4'}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                      )}
                      {header.column.getCanSort() &&
                        !header.isPlaceholder &&
                        header.column.id !== 'select' && (
                          <button
                            className={ctw(
                              'flex h-9 w-full flex-row items-center gap-x-2 text-[#A3A3A3]',
                              header.column.columnDef.meta?.headerClassName || 'justify-start',
                            )}
                            onClick={() => header.column.toggleSorting()}
                          >
                            <span>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                            <ChevronDown
                              className={ctw('d-4', {
                                'rotate-180': header.column.getIsSorted() === 'asc',
                              })}
                            />
                          </button>
                        )}
                      {!header.column.getCanSort() &&
                        !header.isPlaceholder &&
                        header.column.id !== 'select' &&
                        flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody {...props?.body}>
            {!!table.getRowModel().rows?.length &&
              table.getRowModel().rows?.map(row => (
                <React.Fragment key={row.id}>
                  <TableRow
                    key={row.id}
                    {...props?.row}
                    className={ctw(
                      'h-[76px] border-b-0 even:bg-[#F4F6FD]/50 hover:bg-[#F4F6FD]/90',
                      props?.row?.className,
                    )}
                  >
                    {row.getVisibleCells()?.map(cell => (
                      <TableCell
                        key={cell.id}
                        {...props?.cell}
                        className={ctw('!py-px !pl-3.5', props?.cell?.className)}
                      >
                        {CellContentWrapper && !cell.column.columnDef.meta?.useWrapper ? (
                          <CellContentWrapper cell={cell as Cell<TData, TValue>}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </CellContentWrapper>
                        ) : (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {CollapsibleContent && (
                    <Collapsible open={row.getIsExpanded()} asChild>
                      <ShadCNCollapsibleContent asChild>
                        <TableRow className={`max-h-[228px] border-y-[1px]`}>
                          <TableCell colSpan={10} className={`p-8`}>
                            <CollapsibleContent row={row.original} />
                          </TableCell>
                        </TableRow>
                      </ShadCNCollapsibleContent>
                    </Collapsible>
                  )}
                </React.Fragment>
              ))}
            {!table.getRowModel().rows?.length && (
              <TableRow
                {...props?.row}
                className={ctw('hover:bg-unset h-6 border-none', props?.row?.className)}
              >
                <TableCell
                  colSpan={columns?.length}
                  {...props?.noDataCell}
                  className={ctw('p-4', props?.noDataCell?.className)}
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
};

const forward = React.forwardRef as <T, P = NonNullable<unknown>>(
  render: (props: P, ref: React.Ref<T>) => React.ReactNode,
) => (props: P & React.RefAttributes<T>) => React.ReactNode;
export const DataTable = forward(DataTableBase);
